import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useContent } from "@/i18n";
import { Shape3D, HandNote, DoodleArrow } from "@/components/Objects3D";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden pb-3 -mb-3 ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 26 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  const { c, ui } = useContent();
  const profile = c.profile;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yBig = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 3]);

  return (
    <header ref={ref} id="top" className="relative min-h-screen flex flex-col justify-center pt-28 overflow-hidden bg-cream" data-testid="hero-section">
      {/* Oversized pale-blue magazine-cover typography */}
      <motion.span
        aria-hidden="true"
        style={{ y: yBig }}
        className="pointer-events-none select-none absolute -right-6 top-10 font-serif font-bold text-[38vw] lg:text-[30rem] leading-none text-powder/60 z-0"
      >
        S
      </motion.span>

      <div className="max-w-7xl mx-auto px-6 w-full grid lg:grid-cols-12 gap-14 items-center pb-20 relative z-10">
        <div className="lg:col-span-7">
          <motion.p
            {...fadeUp(0.15)}
            className="text-xs tracking-[0.3em] uppercase font-bold text-cocoa flex items-center gap-3"
            data-testid="hero-location"
          >
            <span className="h-px w-10 bg-cobalt" />
            {ui.hero.portfolio} — {profile.location}
          </motion.p>

          <h1 className="mt-8">
            <MaskedLine delay={0.25}>
              <span className="font-hand text-3xl md:text-4xl text-cobalt">{ui.hero.greeting}</span>
            </MaskedLine>
            <MaskedLine delay={0.4} className="font-serif text-[19vw] sm:text-8xl md:text-9xl tracking-tighter leading-[0.9] text-choco">
              Sahithi
            </MaskedLine>
            <MaskedLine delay={0.55} className="font-serif text-[13vw] sm:text-6xl md:text-7xl tracking-tighter leading-[0.95]">
              <span className="italic font-medium text-cobalt">Rasakonda</span>
            </MaskedLine>
          </h1>

          <motion.div {...fadeUp(0.75)} className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="hero-tagline">
            {profile.roles.map((role, i) => (
              <span key={role} className="word-pop flex items-center gap-3 text-xs md:text-sm tracking-[0.14em] uppercase font-bold text-cocoa">
                {i > 0 && <span className={i % 2 === 0 ? "text-cobalt" : "text-choco/40"}>✦</span>}
                {role}
              </span>
            ))}
          </motion.div>

          <motion.p {...fadeUp(0.9)} className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-cocoa" data-testid="hero-statement">
            {profile.statement}
          </motion.p>

          <motion.div {...fadeUp(1.05)} className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#exploring"
              data-testid="hero-explore-button"
              className="group inline-flex items-center gap-3 rounded-full bg-choco text-cream px-7 py-3.5 text-xs tracking-[0.18em] uppercase font-bold hover:bg-cobalt transition-colors duration-400"
            >
              {ui.hero.begin}
              <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
            <a
              href={profile.cvUrl}
              download
              data-testid="hero-cv-button"
              className="group inline-flex items-center gap-3 rounded-full border-2 border-cobalt text-cobalt px-7 py-3 text-xs tracking-[0.18em] uppercase font-bold hover:bg-cobalt hover:text-cream transition-colors duration-400"
            >
              {ui.hero.downloadCv}
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.figure style={{ y, rotate }} className="relative will-change-transform">
            <motion.div
              className="relative mx-auto w-[85%] aspect-[3/4] bg-powder/70 overflow-hidden rounded-[46%_54%_55%_45%/48%_44%_56%_52%] shadow-[0_40px_70px_-30px_rgba(51,38,25,0.35)]"
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.3, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.img
                src={profile.portrait}
                alt={`Portrait of ${profile.name}`}
                data-testid="hero-portrait"
                className="w-full h-full object-cover"
                initial={{ scale: 1.18 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              />
            </motion.div>

            <Shape3D shape="star" blue size={58} duration={5} className="absolute -top-4 -left-2 z-20" />
            <Shape3D shape="cup" size={64} duration={7} delay={0.8} className="absolute top-1/3 -right-5 z-20" mouse rotateAmp={14} />
            <Shape3D shape="book" blue size={54} duration={6} delay={1.4} className="absolute bottom-24 -left-5 z-20" mouse rotateAmp={-12} />

            <motion.div {...fadeUp(1.3)} className="absolute -left-8 top-8 -rotate-6 z-20">
              <HandNote>{ui.hero.note}</HandNote>
              <DoodleArrow className="mt-1 ml-6" />
            </motion.div>

            <motion.figcaption
              {...fadeUp(1.2)}
              className="mt-6 flex items-center justify-between text-xs tracking-[0.2em] uppercase text-cocoa"
            >
              <span>{ui.hero.fig}</span>
              <span className="text-cobalt font-bold">{ui.hero.est}</span>
            </motion.figcaption>
          </motion.figure>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-[max(1.5rem,calc((100vw-80rem)/2+1.5rem))] flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-cocoa z-10"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown size={14} className="text-cobalt" />
        </motion.span>
        {ui.hero.scroll}
      </motion.div>
    </header>
  );
};
